import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { 
  Users, Camera, Briefcase, DollarSign, TrendingUp, Activity,
  Search, Download, Eye, Star, UserX, UserCheck,
  ArrowLeft, Clock, Calendar, CheckCircle, AlertCircle,
  BadgeCheck, Award, KeyRound, FileText, UserPlus, FolderPlus,
  MessageSquare, X, ChevronRight, Mail, MapPin, Image, Trash2
} from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [kpis, setKpis] = useState(null);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [projects, setProjects] = useState({ projects: [], total: 0, page: 1, pages: 1 });
  const [revenueData, setRevenueData] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProfile, setFilterProfile] = useState('all');
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [filterProjectStatus, setFilterProjectStatus] = useState('all');
  const [badgeDialog, setBadgeDialog] = useState({ open: false, user: null });
  const [userDetailDialog, setUserDetailDialog] = useState({ open: false, user: null, activity: null, loading: false });

  const config = { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };

  // Badge configuration
  const badgeOptions = [
    { id: 'trending', label: 'Trending', icon: TrendingUp, description: 'Getting booked frequently' },
    { id: 'verified', label: 'Verified', icon: BadgeCheck, description: 'Vetted by the platform' },
    { id: 'featured', label: 'Featured', icon: Award, description: 'Top creative on the platform' },
    { id: 'top_rated', label: 'Top Rated', icon: Star, description: 'Consistently high ratings' }
  ];

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchKPIs();
    fetchActivity();
    fetchRevenueData();
    fetchFeedback();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [activeTab, searchQuery, filterRole, filterStatus, filterProfile]);

  useEffect(() => {
    if (activeTab === 'projects') fetchProjects();
  }, [activeTab, projectSearchQuery, filterProjectStatus]);

  const fetchKPIs = async () => {
    try {
      const response = await axios.get(`${API}/admin/kpis`, config);
      setKpis(response.data);
    } catch (error) {
      console.error('Failed to fetch KPIs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity?limit=30`, config);
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  const fetchRevenueData = async () => {
    try {
      const response = await axios.get(`${API}/admin/revenue-monthly`, config);
      setRevenueData(response.data);
    } catch (error) {
      console.error('Failed to fetch revenue data:', error);
    }
  };

  const fetchFeedback = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback`, config);
      setFeedback(response.data);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
    }
  };

  const fetchUsers = async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (searchQuery) params.append('search', searchQuery);
      if (filterRole && filterRole !== 'all') params.append('role', filterRole);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterProfile && filterProfile !== 'all') params.append('profile_status', filterProfile);
      const response = await axios.get(`${API}/admin/users?${params}`, config);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchProjects = async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (projectSearchQuery) params.append('search', projectSearchQuery);
      if (filterProjectStatus && filterProjectStatus !== 'all') params.append('status', filterProjectStatus);
      const response = await axios.get(`${API}/admin/projects?${params}`, config);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/activity`, config);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user activity:', error);
      return null;
    }
  };

  const handleUserClick = async (selectedUser) => {
    setUserDetailDialog({ open: true, user: selectedUser, activity: null, loading: true });
    const activityData = await fetchUserActivity(selectedUser.user_id);
    setUserDetailDialog(prev => ({ ...prev, activity: activityData, loading: false }));
  };

  const handleExport = async (type) => {
    try {
      const response = await axios.get(`${API}/admin/export/${type}`, {
        ...config,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} exported successfully`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleUserAction = async (userId, action, value) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { [action]: value }, config);
      toast.success('User updated');
      fetchUsers(users.page);
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleSoftDelete = async (userId) => {
    if (!confirm('Are you sure you want to deactivate this user? They can be reactivated later.')) return;
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: false }, config);
      toast.success('User deactivated');
      fetchUsers(users.page);
    } catch (error) {
      toast.error('Deactivation failed');
    }
  };

  const handleReactivate = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: true }, config);
      toast.success('User reactivated');
      fetchUsers(users.page);
    } catch (error) {
      toast.error('Reactivation failed');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!confirm(`Permanently delete ${userName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, config);
      toast.success('User permanently deleted');
      fetchUsers(users.page);
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleDeleteProject = async (projectId, projectTitle) => {
    if (!confirm(`Delete project "${projectTitle}"? It will be removed from the marketplace and can't be restored by the business.`)) return;
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, config);
      toast.success('Project deleted');
      fetchProjects(projects.page);
      fetchKPIs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleBadgeAction = async (profileId, badge, action) => {
    try {
      await axios.post(`${API}/admin/creatives/${profileId}/badges`, { action, badge }, config);
      toast.success(`Badge ${action === 'add' ? 'added' : 'removed'} successfully`);
      fetchUsers(users.page);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update badge');
    }
  };

  const handleResetUserPassword = async (userId, userEmail) => {
    if (!confirm(`Send password reset email to ${userEmail}?`)) return;
    try {
      await axios.post(`${API}/admin/reset-user-password`, { user_id: userId }, config);
      toast.success(`Password reset email sent to ${userEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    }
  };

  const handleMarkFeedbackRead = async (feedbackId) => {
    try {
      await axios.put(`${API}/admin/feedback/${feedbackId}`, { is_read: true }, config);
      setFeedback(feedback.map(f => f.feedback_id === feedbackId ? { ...f, is_read: true } : f));
    } catch (error) {
      toast.error('Failed to update feedback');
    }
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'signup': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'profile_completed': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'project_created': return <FolderPlus className="w-4 h-4 text-purple-500" />;
      case 'booking_created': return <Calendar className="w-4 h-4 text-orange-500" />;
      case 'request_submitted': return <FileText className="w-4 h-4 text-cyan-500" />;
      case 'payment_completed': return <DollarSign className="w-4 h-4 text-emerald-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  // Calculate max revenue for chart scaling
  const maxRevenue = useMemo(() => {
    return Math.max(...revenueData.map(d => d.revenue), 1);
  }, [revenueData]);

  const unreadFeedbackCount = feedback.filter(f => !f.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-gray-400">Loading admin dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-gray-400 hover:text-black">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">Admin Dashboard</h1>
                <p className="text-sm text-gray-500 hidden sm:block">Platform Overview & Management</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => handleExport('users')} className="text-sm">
              <Download className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Export Users</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200 p-1 rounded-lg w-full sm:w-auto">
            <TabsTrigger value="overview" className="rounded-md px-4 flex-1 sm:flex-none" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" className="rounded-md px-4 flex-1 sm:flex-none" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="projects" className="rounded-md px-4 flex-1 sm:flex-none" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="feedback" className="rounded-md px-4 flex-1 sm:flex-none relative" data-testid="tab-feedback">
              Feedback
              {unreadFeedbackCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadFeedbackCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {kpis && (
              <>
                {/* Marketplace Activity Overview */}
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-gray-600" />
                    Marketplace Activity
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <StatCard label="Total Users" value={kpis.totals.users} icon={<Users className="w-4 h-4" />} />
                    <StatCard label="Creatives" value={kpis.totals.creatives} icon={<Camera className="w-4 h-4" />} color="green" />
                    <StatCard label="Businesses" value={kpis.totals.businesses} icon={<Briefcase className="w-4 h-4" />} color="blue" />
                    <StatCard label="Projects Posted" value={kpis.totals.projects} icon={<FileText className="w-4 h-4" />} color="purple" />
                    <StatCard label="Active Bookings" value={kpis.totals.active_bookings || kpis.activity_7d.bookings} icon={<Calendar className="w-4 h-4" />} color="orange" />
                    <StatCard label="Open Requests" value={kpis.pending.requests} icon={<AlertCircle className="w-4 h-4" />} color="amber" />
                  </div>
                </div>

                {/* Activation Insights */}
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-gray-600" />
                    Activation Insights
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <InsightCard 
                      label="Complete Creative Profiles" 
                      value={kpis.activation?.complete_profiles || 0}
                      total={kpis.totals.creatives}
                      icon={<CheckCircle className="w-5 h-5 text-green-500" />}
                      color="green"
                    />
                    <InsightCard 
                      label="Incomplete Creative Profiles" 
                      value={kpis.activation?.incomplete_profiles || 0}
                      total={kpis.totals.creatives}
                      icon={<AlertCircle className="w-5 h-5 text-amber-500" />}
                      color="amber"
                    />
                    <InsightCard 
                      label="Businesses With No Projects" 
                      value={kpis.activation?.businesses_no_projects || 0}
                      total={kpis.totals.businesses}
                      icon={<Briefcase className="w-5 h-5 text-blue-500" />}
                      color="blue"
                    />
                  </div>
                </div>

                {/* Revenue Over Time */}
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gray-600" />
                    Revenue Over Time
                  </h2>
                  <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200">
                    {revenueData.length > 0 ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>Monthly Revenue (Last 6 months)</span>
                          <span className="font-semibold text-emerald-600">
                            Total: ${kpis.totals.revenue?.toLocaleString() || 0}
                          </span>
                        </div>
                        <div className="flex items-end gap-2 h-40">
                          {revenueData.map((month, idx) => (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                              <div 
                                className="w-full bg-emerald-500 rounded-t-md transition-all hover:bg-emerald-600"
                                style={{ height: `${Math.max((month.revenue / maxRevenue) * 100, 4)}%` }}
                                title={`$${month.revenue.toLocaleString()}`}
                              />
                              <span className="text-xs text-gray-500">{month.label}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between text-xs text-gray-400 pt-2 border-t">
                          {revenueData.map((month, idx) => (
                            <span key={idx} className="flex-1 text-center">${month.revenue.toLocaleString()}</span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No revenue data available yet</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Activity Feed */}
                <div>
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-gray-600" />
                    Recent Activity
                  </h2>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
                      {activity.length > 0 ? activity.map((log) => (
                        <div key={log.log_id} className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors">
                          <div className="mt-0.5">{getEventIcon(log.event_type)}</div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900">{log.description}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(log.created_at).toLocaleString()}
                            </p>
                          </div>
                          {log.amount && (
                            <span className="text-sm font-medium text-emerald-600">
                              ${log.amount.toLocaleString()}
                            </span>
                          )}
                        </div>
                      )) : (
                        <div className="text-center py-12 text-gray-500">
                          <Activity className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No recent activity</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="user-search-input"
                />
              </div>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-36" data-testid="filter-role">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="creative">Creative</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-36" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterProfile} onValueChange={setFilterProfile}>
                <SelectTrigger className="w-40" data-testid="filter-profile">
                  <SelectValue placeholder="Profile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Profiles</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                  <SelectItem value="incomplete">Incomplete</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Name</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Role</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Join Date</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Profile Status</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.users.map((u) => (
                      <tr 
                        key={u.user_id} 
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors" 
                        data-testid={`user-row-${u.user_id}`}
                        onClick={() => handleUserClick(u)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-sm font-medium text-gray-600">
                              {u.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{u.name}</span>
                                <ChevronRight className="w-4 h-4 text-gray-400" />
                              </div>
                              {u.is_admin && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Admin</span>
                              )}
                              {u.badges?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {u.badges.map(badge => (
                                    <span key={badge} className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                      {badge}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            u.role === 'business' ? 'bg-blue-100 text-blue-700' :
                            u.role === 'creative' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>{u.role}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                              u.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {u.is_active !== false ? 'Active' : 'Inactive'}
                            </span>
                            {u.role === 'creative' && (
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                u.profile_complete ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {u.profile_complete ? 'Complete' : 'Incomplete'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {/* Message User */}
                            <Link to={`/messages/${u.user_id}`}>
                              <Button size="sm" variant="ghost" title="Message user" className="text-blue-500">
                                <MessageSquare className="w-4 h-4" />
                              </Button>
                            </Link>

                            {/* View Profile */}
                            {u.role === 'creative' && u.profile_id && (
                              <Link to={`/photographers/${(u.name || 'photographer').replace(/\s+/g, '')}_${u.profile_id.replace('profile_', '')}`}>
                                <Button size="sm" variant="ghost" title="View Profile" data-testid={`view-profile-${u.user_id}`}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </Link>
                            )}
                            
                            {/* Manage Badges (Creatives only) */}
                            {u.role === 'creative' && u.profile_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setBadgeDialog({ open: true, user: u })}
                                title="Manage badges"
                                className="text-purple-600"
                                data-testid={`manage-badges-${u.user_id}`}
                              >
                                <Award className="w-4 h-4" />
                              </Button>
                            )}
                            
                            {/* Make Admin (Business users only) */}
                            {u.role === 'business' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleUserAction(u.user_id, 'is_admin', !u.is_admin)}
                                title={u.is_admin ? 'Remove admin' : 'Make admin'}
                                data-testid={`toggle-admin-${u.user_id}`}
                              >
                                <Star className={`w-4 h-4 ${u.is_admin ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                              </Button>
                            )}
                            
                            {/* Reset Password */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleResetUserPassword(u.user_id, u.email)}
                              title="Send password reset email"
                              data-testid={`reset-password-${u.user_id}`}
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            
                            {/* Deactivate/Reactivate */}
                            {u.is_active !== false ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleSoftDelete(u.user_id)}
                                title="Deactivate user"
                                className="text-red-500 hover:text-red-700"
                                data-testid={`deactivate-${u.user_id}`}
                              >
                                <UserX className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleReactivate(u.user_id)}
                                title="Reactivate user"
                                className="text-green-500 hover:text-green-700"
                                data-testid={`reactivate-${u.user_id}`}
                              >
                                <UserCheck className="w-4 h-4" />
                              </Button>
                            )}

                            {/* Permanently Delete */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteUser(u.user_id, u.name)}
                              title="Permanently delete user"
                              className="text-red-700 hover:text-red-900 hover:bg-red-50"
                              data-testid={`delete-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {users.users.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </div>
              )}
            </div>
            
            {/* Pagination */}
            <Pagination data={users} onPageChange={(p) => fetchUsers(p)} />
          </TabsContent>

          {/* Projects Tab */}
          <TabsContent value="projects" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by title or description..."
                  value={projectSearchQuery}
                  onChange={(e) => setProjectSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="project-search-input"
                />
              </div>
              <Select value={filterProjectStatus} onValueChange={setFilterProjectStatus}>
                <SelectTrigger className="w-40" data-testid="filter-project-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Projects Table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Title</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Posted By</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Budget</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Posted Date</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.projects.map((p) => (
                      <tr
                        key={p.project_id}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        data-testid={`project-row-${p.project_id}`}
                      >
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-medium text-gray-900">{p.title}</span>
                            {p.is_hidden && (
                              <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Deleted</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{p.business_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {p.budget_min != null && p.budget_max != null ? `$${p.budget_min} - $${p.budget_max}` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            p.status === 'open' ? 'bg-green-100 text-green-700' :
                            p.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            p.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                            'bg-red-100 text-red-700'
                          }`}>{p.status}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {!p.is_hidden && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteProject(p.project_id, p.title)}
                                title="Delete project"
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-project-${p.project_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {projects.projects.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No projects found</p>
                </div>
              )}
            </div>

            {/* Pagination */}
            <Pagination data={projects} onPageChange={(p) => fetchProjects(p)} />
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">User Feedback</h2>
              <span className="text-sm text-gray-500">{feedback.length} total submissions</span>
            </div>
            
            <div className="space-y-4">
              {feedback.length > 0 ? feedback.map((f) => (
                <div 
                  key={f.feedback_id} 
                  className={`bg-white rounded-xl p-5 border transition-colors ${
                    f.is_read ? 'border-gray-200' : 'border-blue-300 bg-blue-50/30'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-sm font-medium">
                          {f.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium">{f.user_name || 'Anonymous'}</p>
                          <p className="text-xs text-gray-500">{f.user_email} • {f.user_role}</p>
                        </div>
                        {!f.is_read && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">New</span>
                        )}
                      </div>
                      
                      <div className="mb-3">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          f.category === 'bug' ? 'bg-red-100 text-red-700' :
                          f.category === 'feature' ? 'bg-purple-100 text-purple-700' :
                          f.category === 'improvement' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {f.category || 'General'}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 whitespace-pre-wrap">{f.message}</p>
                      
                      <p className="text-xs text-gray-400 mt-3">
                        {new Date(f.created_at).toLocaleString()}
                      </p>
                    </div>
                    
                    {!f.is_read && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleMarkFeedbackRead(f.feedback_id)}
                        title="Mark as read"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )) : (
                <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No feedback yet</p>
                  <p className="text-sm mt-1">User feedback will appear here</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* User Detail Dialog */}
      <Dialog open={userDetailDialog.open} onOpenChange={(open) => setUserDetailDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center text-lg font-medium">
                {userDetailDialog.user?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="text-xl">{userDetailDialog.user?.name}</p>
                <p className="text-sm font-normal text-gray-500">{userDetailDialog.user?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {userDetailDialog.loading ? (
            <div className="py-12 text-center text-gray-400">
              <div className="animate-pulse">Loading user activity...</div>
            </div>
          ) : userDetailDialog.activity ? (
            <div className="space-y-6 mt-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Role</p>
                  <p className="font-medium capitalize">{userDetailDialog.user?.role}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Joined</p>
                  <p className="font-medium">{new Date(userDetailDialog.user?.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <span className={`text-sm px-2 py-0.5 rounded-full ${
                    userDetailDialog.user?.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {userDetailDialog.user?.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {userDetailDialog.user?.role === 'creative' && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase">Profile</p>
                    <span className={`text-sm px-2 py-0.5 rounded-full ${
                      userDetailDialog.user?.profile_complete ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {userDetailDialog.user?.profile_complete ? 'Complete' : 'Incomplete'}
                    </span>
                  </div>
                )}
              </div>

              {/* Activity Summary */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4" /> Activity Summary
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {userDetailDialog.user?.role === 'business' ? (
                    <>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{userDetailDialog.activity.projects_posted || 0}</p>
                        <p className="text-xs text-gray-500">Projects Posted</p>
                      </div>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{userDetailDialog.activity.bookings_made || 0}</p>
                        <p className="text-xs text-gray-500">Bookings Made</p>
                      </div>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-600">{userDetailDialog.activity.active_bookings || 0}</p>
                        <p className="text-xs text-gray-500">Active Bookings</p>
                      </div>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">${userDetailDialog.activity.total_spent?.toLocaleString() || 0}</p>
                        <p className="text-xs text-gray-500">Total Spent</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-600">{userDetailDialog.activity.applications_sent || 0}</p>
                        <p className="text-xs text-gray-500">Applications</p>
                      </div>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-600">{userDetailDialog.activity.bookings_received || 0}</p>
                        <p className="text-xs text-gray-500">Bookings</p>
                      </div>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-600">{userDetailDialog.activity.active_bookings || 0}</p>
                        <p className="text-xs text-gray-500">Active</p>
                      </div>
                      <div className="p-3 bg-white border border-gray-200 rounded-lg text-center">
                        <p className="text-2xl font-bold text-emerald-600">${userDetailDialog.activity.total_earned?.toLocaleString() || 0}</p>
                        <p className="text-xs text-gray-500">Earned</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="p-3 bg-white border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">{userDetailDialog.activity.messages_sent || 0}</span>
                  <span className="text-gray-500">messages sent</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  <span className="font-medium">{userDetailDialog.activity.reviews_given || 0}</span>
                  <span className="text-gray-500">reviews given</span>
                </div>
              </div>

              {/* Recent Projects (Business) or Portfolio Stats (Creative) */}
              {userDetailDialog.user?.role === 'business' && userDetailDialog.activity.recent_projects?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" /> Recent Projects
                  </h3>
                  <div className="space-y-2">
                    {userDetailDialog.activity.recent_projects.map((project, idx) => (
                      <div key={idx} className="p-3 bg-white border border-gray-200 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium">{project.title}</p>
                          <p className="text-xs text-gray-500">{project.location} • ${project.budget_min}-${project.budget_max}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          project.status === 'open' ? 'bg-green-100 text-green-700' :
                          project.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{project.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Creative Profile Stats */}
              {userDetailDialog.user?.role === 'creative' && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Camera className="w-4 h-4" /> Profile Stats
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4 text-gray-500" />
                        <span className="font-medium">{userDetailDialog.activity.portfolio_items || 0}</span>
                        <span className="text-gray-500 text-sm">portfolio items</span>
                      </div>
                    </div>
                    <div className="p-3 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="font-medium">{userDetailDialog.activity.average_rating?.toFixed(1) || '0.0'}</span>
                        <span className="text-gray-500 text-sm">avg rating ({userDetailDialog.activity.reviews_received || 0} reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Activity Log */}
              {userDetailDialog.activity.recent_activity?.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" /> Recent Activity
                  </h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {userDetailDialog.activity.recent_activity.map((log, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm p-2 bg-gray-50 rounded">
                        <span className="text-gray-400">•</span>
                        <div className="flex-1">
                          <p className="text-gray-700">{log.description}</p>
                          <p className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <p>Could not load user activity</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Badge Management Dialog */}
      <Dialog open={badgeDialog.open} onOpenChange={(open) => setBadgeDialog({ ...badgeDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Badges</DialogTitle>
          </DialogHeader>
          {badgeDialog.user && (
            <div className="space-y-4 mt-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{badgeDialog.user.name}</p>
                <p className="text-sm text-gray-500">{badgeDialog.user.email}</p>
              </div>
              
              <div className="space-y-3">
                {badgeOptions.map((badge) => {
                  const hasBadge = badgeDialog.user.badges?.includes(badge.id);
                  const IconComponent = badge.icon;
                  return (
                    <div
                      key={badge.id}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        hasBadge ? 'border-purple-200 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <IconComponent className={`w-5 h-5 ${hasBadge ? 'text-purple-600' : 'text-gray-400'}`} />
                        <div>
                          <p className={`font-medium ${hasBadge ? 'text-purple-700' : 'text-gray-700'}`}>{badge.label}</p>
                          <p className="text-xs text-gray-500">{badge.description}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={hasBadge ? 'destructive' : 'default'}
                        onClick={() => {
                          handleBadgeAction(badgeDialog.user.profile_id, badge.id, hasBadge ? 'remove' : 'add');
                          setBadgeDialog({
                            ...badgeDialog,
                            user: {
                              ...badgeDialog.user,
                              badges: hasBadge 
                                ? badgeDialog.user.badges.filter(b => b !== badge.id)
                                : [...(badgeDialog.user.badges || []), badge.id]
                            }
                          });
                        }}
                      >
                        {hasBadge ? 'Remove' : 'Add'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color = 'gray' }) => {
  const colorClasses = {
    gray: 'bg-gray-100 text-gray-600',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    amber: 'bg-amber-100 text-amber-600',
    emerald: 'bg-emerald-100 text-emerald-600'
  };
  
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-2 mb-2">
        <span className={`p-1.5 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </span>
      </div>
      <p className="text-2xl font-bold text-gray-900">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
};

// Insight Card Component
const InsightCard = ({ label, value, total, icon, color }) => {
  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
  const colorClasses = {
    green: 'bg-green-50 border-green-200',
    amber: 'bg-amber-50 border-amber-200',
    blue: 'bg-blue-50 border-blue-200'
  };
  
  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]} transition-colors`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-2xl font-bold text-gray-900">{value.toLocaleString()}</p>
          <p className="text-sm text-gray-600 mt-1">{label}</p>
          {total > 0 && (
            <p className="text-xs text-gray-400 mt-2">{percentage}% of total</p>
          )}
        </div>
        {icon}
      </div>
    </div>
  );
};

// Pagination Component
const Pagination = ({ data, onPageChange }) => {
  if (!data || data.pages <= 1) return null;
  
  return (
    <div className="flex items-center justify-between pt-4">
      <p className="text-sm text-gray-500">
        Page {data.page} of {data.pages} ({data.total} total)
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={data.page <= 1}
          onClick={() => onPageChange(data.page - 1)}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={data.page >= data.pages}
          onClick={() => onPageChange(data.page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default AdminDashboard;
